import React, {useEffect, useMemo, useState, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    AlertIcon,
    Box,
    Text,
    Button,
    Link as ChakraLink,
    Container,
    Flex,
    Grid,
    GridItem,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import ContactInfo from './partials/contact-info'
import OrderSummary from '../../components/order-summary/index'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import CheckoutSkeleton from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-skeleton'
import {
    useShopperOrdersMutation,
    useShopperBasketsMutation,
    useProducts
} from '@salesforce/commerce-sdk-react'
import {
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_REMOVED_ITEM_FROM_CART
} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* -----------------Adyen Begin ------------------------ */
import Payment from './partials/payment'
import {AdyenCheckoutProvider, pageTypes} from '@adyen/adyen-salesforce-pwa'
import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import {
    useAccessToken,
    useCustomerId,
    useCustomerType,
    useUsid
} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
/* -----------------Adyen End ------------------------ */
import colors from '../../theme/foundations/colors'
import {pxToRem} from '../../utills/pxToRem'
import {
    amountDueLabel,
    pageHeader,
    placeOrderButtonBelowPaymentStyle,
    placeOrderButtonStyle,
    termsPrivacyInfo,
    termsPrivacyInfoLink
} from './index.style'
import {iconStyle} from '../../components/order-summary/index.style'
import {AmexIcon, DiscoverIcon, MastercardIcon, PaypalIcon, VisaIcon} from '../../components/icons'
import ShippingAddress from './partials/shipping-address'
import ShippingOptions from './partials/shipping-method'
import {CheckoutProvider, useCheckout} from './util/checkout-context'
import useDataLayerProducts from '../../utills/useDataLayerProducts'
import getShipTypeSummary from './util/ship-type-summary'
import {CHECKOUT_PAYPAL} from '../../constants'

const Checkout = () => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {isRegistered} = useCustomerType()
    const {
        step,
        STEPS,
        goToStep,
        hasOnlyEgiftCards,
        selectedPaymentMethod,
        checkoutError,
        setCheckoutError
    } = useCheckout()
    const {usid} = useUsid()
    const {data: customer} = useCurrentCustomer()
    const [error, setError] = useState()
    const {data: basket} = useCurrentBasket()
    const [isLoading, setIsLoading] = useState(false)
    const {res} = useServerContext()
    if (res) {
        res.set('Cache-Control', 'private, no-cache')
    }
    const {mutateAsync: createOrder} = useShopperOrdersMutation('createOrder')
    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {passwordless = {}, social = {}} = getConfig().app.login || {}
    const idps = social?.idps
    const isSocialEnabled = !!social?.enabled
    const isPasswordlessEnabled = !!passwordless?.enabled

    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const dataLayerProducts = useDataLayerProducts(products, basket)

    useEffect(() => {
        if (error || checkoutError || step === 4) {
            window.scrollTo({top: 0})
        }
    }, [error, checkoutError, step])

    useEffect(() => {
        // Clear checkout error when step changes
        if (checkoutError) {
            setCheckoutError(null)
        }
    }, [step])

    const previousStep = useRef(null)
    const email = useMemo(() => {
        return isRegistered ? customer?.email?.toLowerCase() || '' : ''
    }, [isRegistered, customer])

    const sessionInfo = useMemo(
        () => ({
            email,
            session_id: usid
        }),
        [email, usid]
    )

    useEffect(() => {
        if (!step || !usid || !STEPS || !window?.dataLayer) return
        if (!dataLayerProducts || dataLayerProducts.length === 0) return

        if (step === previousStep.current) return
        previousStep.current = step

        const pushDataLayer = (pageType, actionStep, extraEcommerceData = {}) => {
            window.dataLayer.push({
                ...sessionInfo,
                'page-type': pageType
            })

            window.dataLayer.push({
                event: 'checkout',
                ecommerce: {
                    checkout: {
                        actionField: {
                            step: actionStep,
                            shipType: getShipTypeSummary(dataLayerProducts)
                        },
                        products: dataLayerProducts
                    },
                    ...extraEcommerceData
                }
            })
        }

        switch (step) {
            case 1:
                if (STEPS.SHIPPING_ADDRESS) {
                    pushDataLayer('Shipping', 2)
                }
                break
            case 3:
                if (STEPS.PAYMENT) {
                    pushDataLayer('Billing', 3, {
                        checkout_option: {
                            actionField: {
                                step: 2,
                                option: '01'
                            }
                        }
                    })
                }
                break
            case 4:
                if (STEPS.REVIEW_ORDER) {
                    pushDataLayer('Review Order', 4)
                }
                break
            default:
                break
        }
    }, [step, sessionInfo, STEPS, dataLayerProducts])

    const totalGiftCardValue = useMemo(() => {
        if (!basket?.paymentInstruments) return 0

        const total = basket.paymentInstruments.reduce((total, instrument) => {
            if (instrument?.paymentMethodId === 'GIFT_CERTIFICATE') {
                const amount = parseFloat(instrument.amount) || 0 // adjust if `amount` is stored differently
                return total + amount
            }
            return total
        }, 0)
        return Math.round(total * 100) / 100
    }, [basket?.paymentInstruments])

    const amountDue = useMemo(() => {
        return (basket?.orderTotal || basket?.productTotal || 0) - totalGiftCardValue
    }, [basket?.orderTotal, basket?.productTotal, totalGiftCardValue])

    const submitOrder = async () => {
        setIsLoading(true)
        try {
            if (amountDue === 0) {
                if (!basket.paymentInstruments) {
                    try {
                        await addPaymentInstrumentToBasket({
                            parameters: {basketId: basket.basketId},
                            body: {
                                paymentMethodId: 'ADYEN_CUSTOM_SESSION',
                                paymentCard: {cardType: 'Visa'},
                                amount: 0
                            }
                        })
                    } catch (paymentError) {
                        console.error('Payment error', paymentError)
                        throw paymentError
                    }
                }
            }
            const order = await createOrder({
                body: {basketId: basket.basketId}
            })
            if (
                order &&
                ((order.c_kount_Status && order.c_kount_Status === 'DECLINED') ||
                    order.c_authorizePaymentError)
            ) {
                throw new Error('error occured while placing an order')
            }
            navigate(`/checkout/confirmation/${order.orderNo}`)
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            } else {
                errorBody = error?.response?.data
            }
            let message = formatMessage({
                id: 'checkout.message.generic_error',
                defaultMessage: 'An unexpected error occurred during checkout.'
            })
            if (errorBody?.statusCode === 'BUS-1006') {
                message = errorBody?.statusMessage
            }
            setError(message)
        } finally {
            setIsLoading(false)
        }
    }

    const isAllBopisItems = useMemo(() => {
        return basket?.productItems?.every((product) => product?.c_storepickup == true)
    }, [basket?.productItems])

    return (
        <Box flex="1">
            <Container
                data-testid="sf-checkout-container"
                paddingTop={{base: pxToRem(30), lg: pxToRem(50)}}
                paddingBottom={{base: pxToRem(80), lg: pxToRem(80)}}
                paddingLeft={{base: pxToRem(0), lg: pxToRem(45)}}
                paddingRight={{base: pxToRem(0), lg: pxToRem(45)}}
            >
                <Text as="h1" sx={pageHeader}>
                    <FormattedMessage defaultMessage="Checkout" id="checkout.page_heading" />
                </Text>
                <Grid
                    templateColumns={{base: '1fr', lg: '51.56% 1fr'}}
                    gap={{base: pxToRem(60), xl: pxToRem(45)}}
                >
                    <GridItem>
                        <Stack spacing={pxToRem(25)} mx={[pxToRem(11), pxToRem(11), 0, 0]}>
                            {(error || checkoutError) && (
                                <Alert status="error" variant="left-accent">
                                    <AlertIcon />
                                    {error || checkoutError}
                                </Alert>
                            )}

                            <ContactInfo
                                isSocialEnabled={isSocialEnabled}
                                isPasswordlessEnabled={isPasswordlessEnabled}
                                idps={idps}
                            />

                            {!hasOnlyEgiftCards && (
                                <ShippingAddress isAllBopisItems={isAllBopisItems} />
                            )}

                            {!isAllBopisItems && !hasOnlyEgiftCards && <ShippingOptions />}

                            <Payment
                                totalGiftCardValue={totalGiftCardValue}
                                isAllBopisItems={isAllBopisItems}
                            />

                            {step === 4 && (
                                <Box
                                    display={{base: 'none', md: 'block'}}
                                    px={4}
                                    pb={pxToRem(1)}
                                    pl={pxToRem(18)}
                                    pr={pxToRem(18)}
                                    marginTop={`${pxToRem(40)} !important`}
                                    bottom={0}
                                    background="white"
                                    borderColor="gray.100"
                                >
                                    <Text sx={amountDueLabel}>
                                        <FormattedMessage
                                            defaultMessage="Amount Due ${amount} "
                                            id="product_list.button.count.amount_due"
                                            values={{
                                                amount: amountDue?.toFixed(2)
                                            }}
                                        />
                                    </Text>
                                    <Button
                                        sx={placeOrderButtonBelowPaymentStyle}
                                        w="full"
                                        onClick={submitOrder}
                                        isLoading={isLoading}
                                        isDisabled={step !== 4}
                                        marginTop={`${pxToRem(25)} !important`}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Place Order"
                                            id="checkout.button.place_order"
                                        />
                                    </Button>
                                    {/* terms and condition info */}
                                    <Text sx={termsPrivacyInfo}>
                                        <FormattedMessage
                                            id="register_form.message.terms_privacy_info_checkout_page"
                                            defaultMessage="By clicking Place Order, you are agreeing to our <terms>Terms and Conditions</terms>, <policy>Privacy Policy</policy>, and <return>Return Policy</return>."
                                            values={{
                                                policy: (chunks) => (
                                                    <ChakraLink
                                                        as={Link}
                                                        to="/content/privacy-policy.html"
                                                        sx={termsPrivacyInfoLink}
                                                        isExternal
                                                        target="_blank"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                ),
                                                terms: (chunks) => (
                                                    <ChakraLink
                                                        as={Link}
                                                        to="/content/terms.html"
                                                        sx={termsPrivacyInfoLink}
                                                        isExternal
                                                        target="_blank"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                ),
                                                return: (chunks) => (
                                                    <ChakraLink
                                                        as={Link}
                                                        to="/content/returns-exchanges.html"
                                                        sx={termsPrivacyInfoLink}
                                                        isExternal
                                                        target="_blank"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                )
                                            }}
                                        />
                                    </Text>
                                    <Flex justify={'center'} sx={iconStyle}>
                                        <VisaIcon height={8} width={10} mr={2} />
                                        <MastercardIcon height={8} width={10} mr={2} />
                                        <AmexIcon height={8} width={10} mr={2} />
                                        <DiscoverIcon height={8} width={10} mr={2} />
                                        <PaypalIcon height={8} width={10} mr={2} />
                                    </Flex>
                                </Box>
                            )}

                            {/* <Box pt={3} display={{base: 'none', lg: 'block'}}>
                                <Container variant="form">
                                    <Button
                                        w="full"
                                        onClick={submitOrder}
                                        isLoading={isLoading}
                                        sx={placeOrderButtonStyle}
                                        isDisabled={step !== 4}
                                        data-testid="sf-checkout-place-order-btn"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Place Order"
                                            id="checkout.button.place_order"
                                        />
                                    </Button>
                                    <Flex justify={'center'} sx={iconStyle}>
                                        <VisaIcon height={8} width={10} mr={2} />
                                        <MastercardIcon height={8} width={10} mr={2} />
                                        <AmexIcon height={8} width={10} mr={2} />
                                        <DiscoverIcon height={8} width={10} mr={2} />
                                        <PaypalIcon height={8} width={10} mr={2} />
                                    </Flex>
                                </Container>
                            </Box> */}
                        </Stack>
                    </GridItem>

                    <GridItem marginTop={[0, 0, pxToRem(25), pxToRem(25)]}>
                        <Stack
                            boxShadow={`${pxToRem(2)} ${pxToRem(4)} ${pxToRem(16.6)} ${pxToRem(
                                2
                            )} ${colors.blackAlpha[500]}`}
                            paddingLeft={pxToRem(18)}
                            paddingRight={pxToRem(18)}
                            marginTop="0px"
                            pb={selectedPaymentMethod !== 'CC' ? 'unset' : '24px'}
                        >
                            <OrderSummary
                                basket={basket}
                                showTaxEstimationForm={false}
                                showCartItems={true}
                                showPromoCodeForm={true}
                                totalGiftCardValue={totalGiftCardValue}
                                hideNote={true}
                                c_isCart={false}
                                onCheckoutError={(message) => {
                                    if (step > STEPS.CONTACT_INFO) {
                                        setCheckoutError(message)
                                        goToStep(STEPS.CONTACT_INFO)
                                    }
                                }}
                            />
                            {selectedPaymentMethod !== 'CC' && (
                                <Box marginTop="0px !important" display="block">
                                    <Button
                                        sx={placeOrderButtonStyle}
                                        display={['none', 'none', 'block', 'block']}
                                        onClick={submitOrder}
                                        isLoading={isLoading}
                                        isDisabled={step !== 4}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Place Order"
                                            id="checkout.button.place_order"
                                        />
                                    </Button>

                                    <Flex justify={'center'} sx={iconStyle}>
                                        <VisaIcon height={8} width={10} mr={2} />
                                        <MastercardIcon height={8} width={10} mr={2} />
                                        <AmexIcon height={8} width={10} mr={2} />
                                        <DiscoverIcon height={8} width={10} mr={2} />
                                        <PaypalIcon height={8} width={10} mr={2} />
                                    </Flex>
                                </Box>
                            )}
                        </Stack>
                    </GridItem>
                </Grid>
            </Container>
            {selectedPaymentMethod !== 'CC' && (
                <Box
                    display={{base: 'block', md: 'none'}}
                    position="sticky"
                    px={4}
                    pb={pxToRem(1)}
                    bottom={0}
                    background="white"
                    borderTop={`${pxToRem(1)} solid`}
                    borderColor="gray.100"
                >
                    <Container variant="form">
                        <Button
                            sx={placeOrderButtonStyle}
                            w="full"
                            onClick={submitOrder}
                            isLoading={isLoading}
                            isDisabled={step !== 4}
                        >
                            <FormattedMessage
                                defaultMessage="Place Order"
                                id="checkout.button.place_order"
                            />
                        </Button>
                        <Flex justify={'center'} sx={iconStyle}>
                            <VisaIcon height={8} width={10} mr={2} />
                            <MastercardIcon height={8} width={10} mr={2} />
                            <AmexIcon height={8} width={10} mr={2} />
                            <DiscoverIcon height={8} width={10} mr={2} />
                            <PaypalIcon height={8} width={10} mr={2} />
                        </Flex>
                    </Container>
                </Box>
            )}
        </Box>
    )
}
/**
 * Customize Adyen Checkout
 * - Translations
 * - Payment Methods
 * - Execute Callbacks
 */

const checkoutCustomizations = {
    paymentMethodsConfiguration: {
        klarna: {
            useKlarnaWidget: false
        },
        klarna_account: {
            useKlarnaWidget: false
        },
        card: {
            amount: undefined
        },
        googlepay: {
            buttonType: 'order'
        },
        applepay: {
            buttonType: 'order'
        }
    },

    translations: {
        'en-US': {
            'creditCard.numberField.title': 'Card Number',
            'creditCard.expiryDateField.title': 'Expiration Date',
            'creditCard.cvcField.title': 'CCV',
            'creditCard.numberField.placeholder': '',
            'creditCard.holderName.placeholder': '',
            'creditCard.holderName': 'Name on Card',
            payButton: 'Place Order'
        }
    }
}

const CheckoutContainer = () => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {formatMessage} = useIntl()
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const toast = useToast()

    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const navigate = useNavigation()
    const {locale, site} = useMultiSite()
    // const {data: basket} = useCurrentBasket()

    const [authToken, setAuthToken] = useState()

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [customer.customerId])

    if (!authToken) {
        return
    }

    const handleRemoveItem = async (product) => {
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
                        status: 'success'
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                }
            }
        )
    }
    return (
        <AdyenCheckoutProvider
            authToken={authToken}
            customerId={customerId}
            isCustomerRegistered={false}
            locale={locale}
            site={site}
            basket={basket}
            navigate={navigate}
            adyenConfig={checkoutCustomizations}
            returnUrl={`${window.location.origin}/checkout/redirect/handler`}
            page={pageTypes.CHECKOUT}
        >
            {!customer || !customer.customerId || !basket || !basket.basketId ? (
                <CheckoutSkeleton />
            ) : (
                <CheckoutProvider>
                    <Checkout />
                </CheckoutProvider>
            )}
        </AdyenCheckoutProvider>
    )
}

export default CheckoutContainer
